import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Column, Typography } from '@tempmee/react';
import CustomPortal from '../../../commonComponents/CustomPortal';
import { DsPagination } from '../../../commonComponents/DsPagination';
import { Spinner } from '../../../commonComponents/Spinner';
import { TabViewContainer } from '../../../commonComponents/TabViewContainer';
import WarningAlertIcon from '../../../../images/warning_alert_outline.svg';
import { useOfficeReviews } from './hooks';
import { OfficeReviewsTable } from './OfficeReviewsTable';

/**
 * The office profile's Reviews tab.
 *
 * Office-only. Professional profiles keep `UserReview`, which reads the
 * monolith's `admin/users/{id}/reviews` — the only endpoint carrying
 * office→professional reviews. `Profile.js` picks between the two at its two
 * separate `reviewTab` render sites, so there is no shared component and no
 * runtime branch: an office-side change cannot reach the professional page.
 *
 * `TabViewContainer` is kept rather than replaced with a DS heading, because it
 * already renders the frame's heading exactly — 24px semibold in
 * `Colors.neutral_600` with the same 44/38 margins.
 *
 * Paged with a stepper rather than infinite scroll. The endpoint is page-based
 * and reports a total, and a stepper says how many reviews an office has and
 * lets an admin jump to the end — neither of which scrolling offered. It also
 * drops a scroll listener that hung off an ancestor element by id.
 */
export const OfficeReviews = ({ officeId }) => {
  const {
    reviews,
    total,
    page,
    pageSize,
    goToPage,
    isPending,
    isError,
    isFetchingPage,
    refetch,
    removeReview,
  } = useOfficeReviews({ officeId });

  const [selectedReview, setSelectedReview] = useState(null);

  const confirmRemove = () => {
    removeReview(selectedReview?.id);
    setSelectedReview(null);
  };

  const content = () => {
    if (isPending) {
      return (
        <Box py={6}>
          <Spinner />
        </Box>
      );
    }

    // Only when there is nothing on screen to keep. Stepping to a page that
    // fails lands here — React Query drops the placeholder on error, so there
    // are no rows to fall back to. A refresh of the page you are *already* on
    // keeps its stale rows instead, and that case shows the retry row inside
    // the table rather than replacing everything.
    if (isError && reviews.length === 0) {
      return (
        <Alert
          severity="error"
          title="Unable to load reviews"
          description="The review service may be unavailable. Please try again later."
          action={
            <Button variant="outlined" color="secondary" size="small" onClick={refetch}>
              Retry
            </Button>
          }
        />
      );
    }

    if (reviews.length === 0) {
      return (
        <Box py={4}>
          <Typography variant="body1">There are no reviews</Typography>
        </Box>
      );
    }

    return (
      <Column gap={4}>
        <OfficeReviewsTable
          reviews={reviews}
          isFetchingPage={isFetchingPage}
          hasFailedPage={isError}
          onRetry={refetch}
          onRemove={setSelectedReview}
        />

        {/* Hides itself when everything fits one page. */}
        <DsPagination
          page={page}
          pageSize={pageSize}
          totalCount={total}
          onPageChange={goToPage}
        />
      </Column>
    );
  };

  return (
    <>
      <TabViewContainer title="Reviews">{content()}</TabViewContainer>

      {/* Deliberately still `CustomPortal` rather than the design system's
          `ConfirmationModal`: this dialog is not in the GOTU-7338 frame and its
          copy is unchanged, so swapping it would be an unrequested visual
          change to a dialog shared with the professional tab. Worth its own
          ticket alongside the professional revamp. */}
      {selectedReview && (
        <CustomPortal
          Icon={WarningAlertIcon}
          title="Please Confirm"
          text={
            <>
              Do you want to remove the rating for the shift on
              <br />
              {`\n${selectedReview.shiftDateLabel}?`}
            </>
          }
          textStyle={{ marginTop: 5, marginBottom: 20, textAlign: 'center' }}
          submitText="Confirm"
          cancelText="Go Back"
          submitPressed={confirmRemove}
          cancelPressed={() => setSelectedReview(null)}
        />
      )}
    </>
  );
};

OfficeReviews.propTypes = {
  /** The office whose reviews to list. Absent while the profile resolves. */
  officeId: PropTypes.string,
};

OfficeReviews.defaultProps = {
  officeId: undefined,
};

export default OfficeReviews;
